import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  Briefcase,
  ChevronLeft,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Separator } from '@/Components/ui/separator';
import { API_BASE_URL } from '@/config/api';

import { getCompanyPage } from './publicJobsApi';
import { PublicJobDetailDialog } from './PublicJobDetailDialog';
import {
  PublicHeader,
  PublicFooter,
  JobCard,
  ListSkeleton,
  EmptyBlock,
  ErrorBlock,
} from './PublicJobsPage';

/**
 * Company careers page — backs both URL forms:
 *
 *   • Path-based:    /c/:slug          (fallback for old shared links)
 *   • Subdomain:     acme.yourapp.com  (primary, post-subdomain rollout)
 *
 * Inputs:
 *   • Props        slug, jobIdOverride  ← passed when called from
 *                                          PublicEntry on a subdomain
 *   • URL params   slug, jobId          ← classic path-based routing
 *
 * Whichever is supplied wins; we fall back to the URL params so the
 * existing `/c/:slug` routes keep working without changes.
 */
export default function CompanyJobsPage({
  slug: propSlug,
  jobIdOverride,
} = {}) {
  const params = useParams();
  const slug = propSlug ?? params.slug;
  const deepLinkJobId = jobIdOverride ?? params.jobId;
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inline dialog state — initialized from URL so /c/acme/123 opens the
  // dialog for job 123 immediately on first paint.
  const [activeJobId, setActiveJobId] = useState(deepLinkJobId || null);

  // Branding image load-failure guards → graceful fallback to gradient/icon.
  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  useEffect(() => { setLogoError(false); setBannerError(false); }, [slug]);

  const logoUrl = page?.company?.logo
    ? `${API_BASE_URL}/api/hirewithus/company/logo/${page.company.logo}`
    : null;
  const bannerUrl = page?.company?.banner
    ? `${API_BASE_URL}/api/hirewithus/company/banner/${page.company.banner}`
    : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCompanyPage(slug);
        if (!cancelled) setPage(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load company');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Sync deep-link job id from the URL into local state. Lets the user use
  // browser back/forward to navigate between jobs within the company page.
  useEffect(() => {
    setActiveJobId(deepLinkJobId || null);
  }, [deepLinkJobId]);

  const openJob = (job) => {
    setActiveJobId(job.job_id);
    // Push to URL so the back button works as expected
    navigate(`/c/${slug}/${job.job_id}`, { replace: false });
  };

  const closeJob = () => {
    setActiveJobId(null);
    navigate(`/c/${slug}`, { replace: false });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950">
      <PublicHeader
        companyName={page?.company?.name || null}
        companyLogoUrl={logoUrl}
      />

      {/* Company header — branded with the employer's banner + logo. Falls
          back to a subtle gradient + generic icon when either asset is
          missing or fails to load, so companies that haven't uploaded still
          look intentional. */}
      <section className="border-b border-border/40 bg-card">
        {/* Banner band */}
        <div className="relative h-36 w-full overflow-hidden bg-gradient-to-r from-primary/15 via-primary/5 to-transparent sm:h-48">
          {bannerUrl && !bannerError && (
            <img
              src={bannerUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setBannerError(true)}
            />
          )}
          <div className="absolute left-0 right-0 top-0 mx-auto max-w-6xl px-4 pt-4">
            <Button
              variant="secondary"
              size="sm"
              className="-ml-1 bg-white/80 text-foreground backdrop-blur hover:bg-white"
              onClick={() => navigate('/jobs')}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              All jobs
            </Button>
          </div>
        </div>

        {/* Logo overlaps the banner's bottom edge; the name sits BELOW it in
            the white area so nothing is clipped or hidden against the banner. */}
        <div className="mx-auto max-w-6xl px-4 pb-6">
          <div className="relative z-10 -mt-12 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-4 border-card bg-card shadow-md sm:h-28 sm:w-28">
            {logoUrl && !logoError ? (
              <img
                src={logoUrl}
                alt={page?.company?.name || ''}
                className="h-full w-full object-contain p-1.5"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary">
                <Building2 className="h-10 w-10" />
              </div>
            )}
          </div>
          <div className="mt-4">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {page?.company?.name || (loading ? 'Loading…' : slug)}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {page?.company?.job_count
                ? `${page.company.job_count} open role${page.company.job_count === 1 ? '' : 's'}`
                : (loading ? '' : 'Careers')}
            </p>
          </div>
        </div>
      </section>

      {/* Jobs grid */}
      <section className="mx-auto max-w-6xl px-4 py-8">
        {loading ? (
          <ListSkeleton />
        ) : error ? (
          <ErrorBlock message={error} />
        ) : !page || !page.jobs || page.jobs.length === 0 ? (
          <Card className="border border-dashed border-border/40">
            <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
              <Briefcase className="h-12 w-12 text-muted-foreground/50" />
              <h3 className="text-base font-semibold">No open roles</h3>
              <p className="text-sm text-muted-foreground">
                This company doesn't have any open positions right now.
              </p>
              <Button variant="outline" onClick={() => navigate('/jobs')}>
                Browse other jobs
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Separator className="mb-4" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {page.jobs.map((job) => (
                <JobCard
                  key={job.job_id}
                  job={job}
                  onClick={() => openJob(job)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <PublicFooter />

      {/* Detail dialog */}
      {activeJobId && (
        <PublicJobDetailDialog
          jobId={activeJobId}
          companySlug={slug}
          onClose={closeJob}
        />
      )}
    </div>
  );
}
